import { useParams } from "react-router";
import {
  useQpmOfferCreateOfferConfigTextBlockSnapshotMutation,
  useQpmOfferDeleteOfferConfigTextBlockSnapshotMutation,
  useQpmOfferPatchOfferConfigTextBlockSnapshotAlwaysDisplayMutation,
  useQpmOfferPatchOfferConfigTextBlockSnapshotBodyMutation,
  useQpmOfferPatchOfferConfigTextBlockSnapshotTitleMutation,
  useQpmOfferPutCoverLetterTextBlocksMutation,
  useQpmOfferPutOfferConfigSettingsSnapshotMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
  useQpmOfferReorderOfferConfigTextBlocksSnapshotMutation,
  useQpmOfferShowQuery,
} from "@/services/backend/qpm/offers/api/offer-api";
import {
  OfferConfigTextBlockSnapshot,
  OfferCoverLetterTextBlocks,
  PutCoverLetterTextBlocksRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { OfferConfigTextBlockSection } from "@/services/backend/qpm/offer-config/types/offer-config-enum";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Separator } from "@/shared/components/ui/separator";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { GripVertical, Pencil, Plus } from "lucide-react";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "@/shared/components/editor/editor";
import { Label } from "@/shared/components/ui/label";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  fieldErrorsForPrefix,
  resetField,
} from "@/shared/lib/errors/validation-error";

export function CoverLetterRoute() {
  const params = useParams();
  const { offerId } = params;
  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId! }, { skip: !offerId });
  const { validationError, setValidationError } = useOfferContext();

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  const textBlocks =
    offer?.offerConfigSnapshot?.textBlocks?.filter(
      (tb) =>
        tb.section === OfferConfigTextBlockSection.CoverLetter &&
        tb.alwaysDisplay,
    ) ?? [];

  return (
    offer && (
      <Card className="mb-12">
        <CardHeader className="py-4 font-bold">
          <div className="flex items-center justify-between">
            <CardTitle>
              {t("Angebot")}
              {" - "}
              <DocumentNumberById className="text-xl" documentId={offer.id} />
            </CardTitle>
          </div>
        </CardHeader>
        <div className="mx-6 mb-2">
          <Separator />
        </div>
        <CardContent>
          <CoverLetterSalutationBox
            offerId={offer.id}
            coverLetterText={offer.coverLetterText}
            disabled={offer.immutable}
            fieldErrors={fieldErrorsForPrefix(
              validationError,
              "coverLetterText.",
            )}
            resetFieldError={(field) =>
              setValidationError(
                resetField(validationError, `coverLetterText.${field}`),
              )
            }
          />
          <div className="my-6">
            <Separator />
          </div>
          <OfferCoverLetterTextBlocksSnapshot
            offerId={offer.id}
            textBlocks={textBlocks}
            disabled={offer.immutable}
            fieldErrors={fieldErrorsForPrefix(
              validationError,
              "offerConfigSnapshot.textBlocks",
            )}
          />
        </CardContent>
      </Card>
    )
  );
}

function CoverLetterSalutationBox({
  offerId,
  coverLetterText,
  disabled,
  fieldErrors,
  resetFieldError,
}: {
  offerId: string;
  coverLetterText: OfferCoverLetterTextBlocks;
  disabled?: boolean;
  fieldErrors?: Record<string, string[]>;
  resetFieldError?: (field: string) => void;
}) {
  const [coverLetterSalutation, setCoverLetterSalutation] = useState<string>(
    coverLetterText.coverLetterSalutation,
  );

  // Hydrate from server whenever we switch offers or server data changes.
  useEffect(() => {
    setCoverLetterSalutation(coverLetterText.coverLetterSalutation);
  }, [offerId, coverLetterText.coverLetterSalutation]);

  const [putText, { error, isLoading, isSuccess, reset }] =
    useQpmOfferPutCoverLetterTextBlocksMutation();

  const request = useMemo<PutCoverLetterTextBlocksRequest>(
    () => ({
      offerId,
      coverLetterSalutation,
      // Preserve other fields from server state (not editable here)
      coverLetterCustomText: coverLetterText.coverLetterCustomText,
      coverLetterConceptText: coverLetterText.coverLetterConceptText,
      coverLetterCustomerRequirements:
        coverLetterText.coverLetterCustomerRequirements,
    }),
    [
      coverLetterSalutation,
      coverLetterText.coverLetterConceptText,
      coverLetterText.coverLetterCustomText,
      coverLetterText.coverLetterCustomerRequirements,
      offerId,
    ],
  );

  const hasChanges =
    coverLetterSalutation !== coverLetterText.coverLetterSalutation;

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    putText,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: true, skip: disabled || !hasChanges },
  );

  return (
    <div className="flex flex-col gap-3">
      <CoverLetterTextField
        label={t("Anrede")}
        value={coverLetterSalutation}
        disabled={disabled}
        errorMessages={fieldErrors?.coverLetterSalutation}
        onChange={(val) => {
          resetFieldError?.("coverLetterSalutation");
          setCoverLetterSalutation(val);
        }}
      />

      {error && <RTKQueryErrorAlert error={error} />}
    </div>
  );
}

function CoverLetterTextField({
  label,
  value,
  onChange,
  disabled,
  errorMessages,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  errorMessages?: string[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-lg">{label}</Label>
      <Input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {errorMessages?.length ? (
        <p className="text-destructive text-sm">{errorMessages.join(" ")}</p>
      ) : null}
    </div>
  );
}

function OfferCoverLetterTextBlocksSnapshot({
  offerId,
  textBlocks,
  fieldErrors,
  disabled,
}: {
  offerId: string;
  textBlocks: OfferConfigTextBlockSnapshot[];
  fieldErrors?: Record<string, string[]>;
  disabled?: boolean;
}) {
  const { validationError, setValidationError } = useOfferContext();

  const clearTextBlocksValidationErrors = useCallback(() => {
    let next = resetField(validationError, "offerConfigSnapshot.textBlocks");

    const nestedKeys = Object.keys(validationError?.errors ?? {}).filter((k) =>
      k.startsWith("offerConfigSnapshot.textBlocks."),
    );

    nestedKeys.forEach((key) => {
      next = resetField(next, key);
    });

    setValidationError(next);
  }, [setValidationError, validationError]);

  const hasAnyFieldErrors = (errs?: Record<string, string[]>) =>
    !!errs && Object.values(errs).some((v) => (v?.length ?? 0) > 0);

  const renderFieldErrors = (errs?: Record<string, string[]>) =>
    Object.entries(errs ?? {})
      .flatMap(([field, list]) => (list ?? []).map((msg) => ({ field, msg })))
      .map(({ field, msg }) => <div key={`${field}:${msg}`}>{msg}</div>);

  const [patchBody, patchBodyState] =
    useQpmOfferPatchOfferConfigTextBlockSnapshotBodyMutation();
  const [patchTitle, patchTitleState] =
    useQpmOfferPatchOfferConfigTextBlockSnapshotTitleMutation();
  const [patchAlwaysDisplay, patchAlwaysDisplayState] =
    useQpmOfferPatchOfferConfigTextBlockSnapshotAlwaysDisplayMutation();
  const [reorder, reorderState] =
    useQpmOfferReorderOfferConfigTextBlocksSnapshotMutation();
  const [createTextBlock, createTextBlockState] =
    useQpmOfferCreateOfferConfigTextBlockSnapshotMutation();
  const [reloadSnapshot, reloadSnapshotState] =
    useQpmOfferReloadOfferConfigSnapshotMutation();
  const [deleteTextBlockSnapshot, deleteTextBlockSnapshotState] =
    useQpmOfferDeleteOfferConfigTextBlockSnapshotMutation();

  const [putSettingsSnapshot, putSettingsSnapshotState] =
    useQpmOfferPutOfferConfigSettingsSnapshotMutation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [editBody, setEditBody] = useState<string>("");

  const [plusDialogOpen, setPlusDialogOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number>(0);

  const [unusedSelectId, setUnusedSelectId] = useState<string>("");
  // Load also unused blocks for selection in dialog (same section but alwaysDisplay=false)
  const { data: offerAll } = useQpmOfferShowQuery(
    { id: offerId },
    { skip: !offerId },
  );
  const allCoverLetterBlocks = useMemo(() => {
    const all = offerAll?.offerConfigSnapshot?.textBlocks ?? [];
    return all.filter(
      (b) => b.section === OfferConfigTextBlockSection.CoverLetter,
    );
  }, [offerAll?.offerConfigSnapshot?.textBlocks]);

  const unusedBlocks = useMemo(
    () => allCoverLetterBlocks.filter((b) => !b.alwaysDisplay),
    [allCoverLetterBlocks],
  );

  const selectedUnused = useMemo(
    () => unusedBlocks.find((b) => b.id === unusedSelectId) ?? null,
    [unusedBlocks, unusedSelectId],
  );

  const [newTitle, setNewTitle] = useState<string>("");
  const [newBody, setNewBody] = useState<string>("");
  const [newAlwaysDisplay, setNewAlwaysDisplay] = useState<boolean>(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const startEdit = useCallback((tb: OfferConfigTextBlockSnapshot) => {
    setEditingId(tb.id);
    setEditTitle(tb.title ?? "");
    setEditBody(tb.body ?? "");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditTitle("");
    setEditBody("");
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (disabled) {
        return;
      }
      if (reorderState.isLoading) return;

      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const orderedIds = textBlocks.map((i) => i.id);
      const oldIndex = orderedIds.findIndex((id) => id === active.id);
      const newIndex = orderedIds.findIndex((id) => id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(orderedIds, oldIndex, newIndex);
      try {
        await reorder({
          offerId,
          section: OfferConfigTextBlockSection.CoverLetter,
          orderedIds: next,
        }).unwrap();
      } catch {
        // Keep UI stable on transient reorder failures.
      }
    },
    [disabled, offerId, reorder, reorderState.isLoading, textBlocks],
  );

  const isSaving = patchBodyState.isLoading || patchTitleState.isLoading;

  const openPlusDialogAt = useCallback(
    (idx: number) => {
      if (disabled) {
        return;
      }
      setInsertIndex(idx);
      setUnusedSelectId("");
      setNewTitle("");
      setNewBody("");
      setNewAlwaysDisplay(true);
      setPlusDialogOpen(true);
    },
    [disabled],
  );

  const reorderToInsert = useCallback(
    async (newOrUpdatedId: string, idx: number) => {
      // Use currently displayed order plus the inserted one
      const current = textBlocks.map((b) => b.id);
      const list = current.includes(newOrUpdatedId)
        ? current.filter((id) => id !== newOrUpdatedId)
        : [...current];
      list.splice(Math.min(Math.max(idx, 0), list.length), 0, newOrUpdatedId);
      await reorder({
        offerId,
        section: OfferConfigTextBlockSection.CoverLetter,
        orderedIds: list,
      }).unwrap();
    },
    [offerId, reorder, textBlocks],
  );

  const handleAddExistingUnused = useCallback(async () => {
    if (disabled) {
      return;
    }
    if (!unusedSelectId) return;
    try {
      await patchAlwaysDisplay({
        offerId,
        id: unusedSelectId,
        alwaysDisplay: true,
      }).unwrap();
      await reorderToInsert(unusedSelectId, insertIndex);
      clearTextBlocksValidationErrors();
      setPlusDialogOpen(false);
    } catch {
      // Keep dialog open and avoid crashing the route on add errors.
    }
  }, [
    disabled,
    insertIndex,
    offerId,
    patchAlwaysDisplay,
    reorderToInsert,
    unusedSelectId,
    clearTextBlocksValidationErrors,
  ]);

  const reorderVisibleBlocks = useCallback(
    async (visibleIds: string[]) => {
      await reorder({
        offerId,
        section: OfferConfigTextBlockSection.CoverLetter,
        orderedIds: visibleIds,
      }).unwrap();
    },
    [offerId, reorder],
  );

  const handleCreateNew = useCallback(async () => {
    if (disabled) {
      return;
    }
    try {
      const created = await createTextBlock({
        offerId,
        section: OfferConfigTextBlockSection.CoverLetter,
        title: newTitle,
        body: newBody,
        alwaysDisplay: newAlwaysDisplay,
      }).unwrap();

      const returnedCoverLetter =
        created.offerConfigSnapshot?.textBlocks?.filter(
          (b) => b.section === OfferConfigTextBlockSection.CoverLetter,
        ) ?? [];

      const previousIds = new Set(allCoverLetterBlocks.map((tb) => tb.id));
      const createdBlock = returnedCoverLetter.find(
        (tb) => !previousIds.has(tb.id),
      );

      if (createdBlock?.alwaysDisplay) {
        const returnedVisibleIds = returnedCoverLetter
          .filter((b) => b.alwaysDisplay)
          .sort((a, b) => a.order - b.order)
          .map((b) => b.id)
          .filter((id) => id !== createdBlock.id);

        const idx = Math.min(
          Math.max(insertIndex, 0),
          returnedVisibleIds.length,
        );
        returnedVisibleIds.splice(idx, 0, createdBlock.id);

        await reorderVisibleBlocks(returnedVisibleIds).catch(() => undefined);
      }

      clearTextBlocksValidationErrors();
      setPlusDialogOpen(false);
    } catch {
      // Keep dialog open and avoid crashing the route on create errors.
    }
  }, [
    disabled,
    allCoverLetterBlocks,
    createTextBlock,
    insertIndex,
    newAlwaysDisplay,
    newBody,
    newTitle,
    offerId,
    reorderVisibleBlocks,
    clearTextBlocksValidationErrors,
  ]);

  const formatTextBlockSelectLabel = useCallback(
    (b: { title?: string; body?: string }) => {
      const title = (b.title ?? "").trim();
      if (title.length > 0) return title;

      const plain = (b.body ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      if (plain.length === 0) return t("(Ohne Titel)");

      const words = plain.split(" ");
      const short = words.slice(0, 6).join(" ");
      const preview =
        words.length > 6 || plain.length > short.length ? `${short}…` : short;

      return `${t("(Ohne Titel)")} – ${preview}`;
    },
    [],
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(
    null,
  );

  const openDeleteDialog = useCallback((id: string) => {
    setDeleteCandidateId(id);
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteCandidateId(null);
  }, []);

  const handlePermanentDelete = useCallback(async () => {
    if (disabled) {
      return;
    }
    if (!deleteCandidateId) return;
    try {
      await deleteTextBlockSnapshot({
        offerId,
        id: deleteCandidateId,
      }).unwrap();
      // keep ordering stable; backend should remove it, but reorder visible ids as safety.
      const remainingVisibleIds = textBlocks
        .filter((b) => b.id !== deleteCandidateId)
        .map((b) => b.id);
      if (remainingVisibleIds.length > 0) {
        await reorderVisibleBlocks(remainingVisibleIds);
      }
      closeDeleteDialog();
      cancelEdit();
    } catch {
      // Keep dialog open and avoid crashing the route on delete errors.
    }
  }, [
    disabled,
    cancelEdit,
    closeDeleteDialog,
    deleteCandidateId,
    deleteTextBlockSnapshot,
    offerId,
    reorderVisibleBlocks,
    textBlocks,
  ]);

  const handleOnlyHide = useCallback(async () => {
    if (disabled) {
      return;
    }
    if (!deleteCandidateId) return;
    const remainingVisibleIds = textBlocks
      .filter((b) => b.id !== deleteCandidateId)
      .map((b) => b.id);

    try {
      await patchAlwaysDisplay({
        offerId,
        id: deleteCandidateId,
        alwaysDisplay: false,
      }).unwrap();

      await reorderVisibleBlocks(remainingVisibleIds);

      closeDeleteDialog();
      cancelEdit();
    } catch {
      // Keep dialog open and avoid crashing the route on hide errors.
    }
  }, [
    disabled,
    cancelEdit,
    closeDeleteDialog,
    deleteCandidateId,
    offerId,
    patchAlwaysDisplay,
    reorderVisibleBlocks,
    textBlocks,
  ]);

  const handleSaveEdit = useCallback(
    async (id: string) => {
      if (disabled) {
        return;
      }
      const current = textBlocks.find((tb) => tb.id === id);
      const titleChanged = (current?.title ?? "") !== editTitle;
      const bodyChanged = (current?.body ?? "") !== editBody;

      if (!titleChanged && !bodyChanged) {
        cancelEdit();
        return;
      }

      try {
        if (bodyChanged) {
          await patchBody({ offerId, id, body: editBody }).unwrap();
        }
        if (titleChanged) {
          await patchTitle({ offerId, id, title: editTitle }).unwrap();
        }
        cancelEdit();
      } catch {
        // Keep edit mode open and avoid crashing the route on save errors.
      }
    },
    [
      disabled,
      textBlocks,
      editTitle,
      editBody,
      cancelEdit,
      patchBody,
      patchTitle,
      offerId,
    ],
  );

  const makeAlwaysDisplayToggler = useCallback(
    (id: string) => async (next: boolean) => {
      if (disabled) {
        return;
      }
      try {
        if (next) {
          await patchAlwaysDisplay({
            offerId,
            id,
            alwaysDisplay: true,
          }).unwrap();
          return;
        }

        const remainingVisibleIds = textBlocks
          .filter((b) => b.id !== id)
          .map((b) => b.id);

        await patchAlwaysDisplay({
          offerId,
          id,
          alwaysDisplay: false,
        }).unwrap();
        await reorderVisibleBlocks(remainingVisibleIds);
      } catch {
        // Keep UI stable on toggle failures.
      }
    },
    [disabled, offerId, patchAlwaysDisplay, reorderVisibleBlocks, textBlocks],
  );

  const [reloadConfirmOpen, setReloadConfirmOpen] = useState(false);

  const openReloadConfirm = useCallback(() => {
    setReloadConfirmOpen(true);
  }, []);

  const closeReloadConfirm = useCallback(() => {
    setReloadConfirmOpen(false);
  }, []);

  const confirmReloadSnapshot = useCallback(async () => {
    if (disabled) {
      return;
    }
    try {
      await reloadSnapshot({ offerId }).unwrap();
      setReloadConfirmOpen(false);
    } catch {
      // Keep dialog open and avoid crashing the route on reload errors.
    }
  }, [disabled, offerId, reloadSnapshot]);

  return (
    <div className="flex flex-col gap-4">
      {hasAnyFieldErrors(fieldErrors) ? (
        <div className="text-destructive text-sm">
          {renderFieldErrors(fieldErrors)}
        </div>
      ) : null}

      <DeleteTextBlockDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onCancel={closeDeleteDialog}
        onOnlyHide={handleOnlyHide}
        onPermanentDelete={handlePermanentDelete}
        isHideDisabled={
          patchAlwaysDisplayState.isLoading || reorderState.isLoading
        }
        isDeleteDisabled={deleteTextBlockSnapshotState.isLoading}
      />

      <Dialog open={reloadConfirmOpen} onOpenChange={setReloadConfirmOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("Stammdaten neu laden")}</DialogTitle>
            <DialogDescription>
              {t(
                "Achtung: Beim Neuladen der Stammdaten werden alle individuell hinzugefügten Textblöcke und Texte dauerhaft entfernt.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="text-muted-foreground text-sm">
            {t(
              "Dieser Vorgang kann nicht rückgängig gemacht werden. Möchten Sie wirklich fortfahren?",
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeReloadConfirm}>
              {t("Abbrechen")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmReloadSnapshot}
              disabled={reloadSnapshotState.isLoading}
            >
              {t("Stammdaten neu laden")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={plusDialogOpen} onOpenChange={setPlusDialogOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] overflow-y-auto sm:w-full sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("Textblock hinzufügen")}</DialogTitle>
            <DialogDescription>
              {t(
                "Wähle einen aktuell nicht verwendeten Textblock oder erstelle einen neuen.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium">
                {t("Nicht verwendete Textblöcke")}
              </div>
              <Select
                value={unusedSelectId}
                onValueChange={setUnusedSelectId}
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("Bitte wählen")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {unusedBlocks.length === 0 && (
                      <SelectItem value="__none" disabled>
                        {t("Keine verfügbar")}
                      </SelectItem>
                    )}
                    {unusedBlocks.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {formatTextBlockSelectLabel(b)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {selectedUnused && (
              <div className="rounded-md border p-3">
                <div className="mb-2 text-sm font-medium">
                  {selectedUnused.title || t("(Ohne Titel)")}
                </div>
                <div
                  className="rounded bg-white text-sm"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: selectedUnused.body || "",
                  }}
                />
                <div className="mt-3 flex justify-end">
                  <Button
                    variant="default"
                    disabled={
                      disabled ||
                      patchAlwaysDisplayState.isLoading ||
                      reorderState.isLoading
                    }
                    onClick={handleAddExistingUnused}
                  >
                    {t("Wieder hinzufügen")}
                  </Button>
                </div>
              </div>
            )}

            <Separator />

            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium">
                {t("Neuen Textblock erstellen")}
              </div>
              <Input
                value={newTitle}
                placeholder={t("Titel")}
                disabled={disabled}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              <div className="max-h-[70vh] min-h-0 overflow-y-auto sm:max-h-[55vh]">
                <TipTapHtmlEditor
                  value={newBody}
                  onChange={(val) => {
                    if (disabled) return;
                    setNewBody(val);
                  }}
                  disabled={disabled || createTextBlockState.isLoading}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={newAlwaysDisplay}
                  disabled={disabled}
                  onCheckedChange={(v) => {
                    if (disabled) return;
                    setNewAlwaysDisplay(!!v);
                  }}
                />
                <div className="text-sm">{t("Standardmäßig anzeigen")}</div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => {
                if (disabled) return;
                const settings = offerAll?.offerConfigSnapshot?.settings;
                if (!settings) return;
                await putSettingsSnapshot({
                  offerId,
                  ceoName: settings.ceoName ?? "",
                  ceoGender: settings.ceoGender ?? "",
                  allowedDiscounts: settings.allowedDiscounts ?? [],
                  allowedSurcharges: settings.allowedSurcharges ?? [],
                  offerClaimText: settings.offerClaimText ?? "",
                  serviceProgramGroups: settings.serviceProgramGroups ?? [],
                  serviceProgramEntries: settings.serviceProgramEntries ?? [],
                  serviceProgramPageBreaks:
                    settings.serviceProgramPageBreaks ?? [],
                  serviceProgramPageBreaksInitialized: true,
                }).unwrap();
              }}
              disabled={disabled || putSettingsSnapshotState.isLoading}
            >
              {t("Settings speichern")}
            </Button>
            <Button
              onClick={handleCreateNew}
              disabled={
                disabled ||
                createTextBlockState.isLoading ||
                reorderState.isLoading
              }
            >
              {t("Erstellen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={textBlocks.map((tb) => tb.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col">
            <PlusInsertButton
              onClick={() => openPlusDialogAt(0)}
              disabled={disabled}
            />

            {textBlocks.map((tb, idx) => (
              <div key={tb.id} className="flex flex-col">
                {idx > 0 && (
                  <PlusInsertButton
                    onClick={() => openPlusDialogAt(idx)}
                    disabled={disabled}
                  />
                )}

                <div className="py-1">
                  <OfferConfigTextBlockSnapshotCard
                    textBlock={tb}
                    isEditing={editingId === tb.id}
                    editTitle={editTitle}
                    editBody={editBody}
                    onStartEdit={() => startEdit(tb)}
                    onCancelEdit={cancelEdit}
                    onChangeTitle={setEditTitle}
                    onChangeBody={setEditBody}
                    isSaving={isSaving}
                    onSave={() => handleSaveEdit(tb.id)}
                    isAlwaysDisplayUpdating={patchAlwaysDisplayState.isLoading}
                    onToggleAlwaysDisplay={makeAlwaysDisplayToggler(tb.id)}
                    onRequestDelete={() => openDeleteDialog(tb.id)}
                    disabled={disabled}
                  />
                </div>
              </div>
            ))}

            <PlusInsertButton
              onClick={() => openPlusDialogAt(textBlocks.length)}
              disabled={disabled}
            />
          </div>
        </SortableContext>
      </DndContext>

      <div className="flex justify-end pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={openReloadConfirm}
          disabled={disabled || reloadSnapshotState.isLoading}
        >
          {t("Stammdaten neu laden")}
        </Button>
      </div>
    </div>
  );
}

function OfferConfigTextBlockSnapshotCard({
  textBlock,
  isEditing,
  editTitle,
  editBody,
  onStartEdit,
  onCancelEdit,
  onChangeTitle,
  onChangeBody,
  onSave,
  isSaving,
  onToggleAlwaysDisplay,
  isAlwaysDisplayUpdating,
  onRequestDelete,
  disabled,
}: {
  textBlock: OfferConfigTextBlockSnapshot;
  isEditing: boolean;
  editTitle: string;
  editBody: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeTitle: (title: string) => void;
  onChangeBody: (body: string) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
  onToggleAlwaysDisplay: (next: boolean) => Promise<void>;
  isAlwaysDisplayUpdating: boolean;
  onRequestDelete: () => void;
  disabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: textBlock.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleToggleAlwaysDisplay = useCallback(
    (next: boolean) => {
      if (disabled) return;
      if (isEditing) return;
      onToggleAlwaysDisplay(next).catch(() => {
        // handled via RTK error alerts upstream
      });
    },
    [disabled, isEditing, onToggleAlwaysDisplay],
  );

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <Button
            {...attributes}
            {...listeners}
            type="button"
            variant="ghost"
            size="icon"
            className="cursor-grab"
            title={t("Verschieben")}
            disabled={disabled}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </Button>
          <div className="text-sm font-medium">
            {textBlock.title || t("(Ohne Titel)")}
          </div>
          <div className="text-muted-foreground text-sm">
            {t("Reihenfolge")}: {textBlock.order}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hover:bg-muted rounded p-1">
            <Checkbox
              checked={textBlock.alwaysDisplay}
              disabled={disabled || isAlwaysDisplayUpdating || isEditing}
              onCheckedChange={(v) => {
                const next = !!v;
                handleToggleAlwaysDisplay(next);
              }}
            />
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={onStartEdit}
            disabled={disabled}
            title={t("Bearbeiten")}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-3">
        {isEditing ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium">{t("Titel")}</div>
              <Input
                value={editTitle}
                disabled={disabled}
                onChange={(e) => onChangeTitle(e.target.value)}
                placeholder={t("Titel")}
              />
            </div>
            <TipTapHtmlEditor
              value={editBody}
              onChange={(val) => {
                if (disabled) return;
                onChangeBody(val);
              }}
              disabled={disabled || isSaving}
              editable
              showToolbar
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={onSave}
                disabled={disabled || isSaving}
              >
                {t("Speichern")}
              </Button>
              <Button size="sm" variant="outline" onClick={onCancelEdit}>
                {t("Abbrechen")}
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="destructive"
                type="button"
                onClick={onRequestDelete}
                disabled={disabled}
              >
                {t("Löschen")}
              </Button>
            </div>
          </div>
        ) : (
          <TipTapHtmlEditor
            value={textBlock.body || ""}
            editable={false}
            showToolbar={false}
          />
        )}
      </div>
    </div>
  );
}

type PlusInsertButtonProps = {
  onClick: () => void;
};

function PlusInsertButton({
  onClick,
  disabled,
}: PlusInsertButtonProps & { disabled?: boolean }) {
  return (
    <div className="flex items-center justify-center py-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-muted-foreground h-6 w-6"
        onClick={onClick}
        disabled={disabled}
        title={t("Block hinzufügen")}
      >
        <Plus className="h-2.5 w-2.5" />
      </Button>
    </div>
  );
}

type DeleteTextBlockDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onOnlyHide: () => void;
  onPermanentDelete: () => void;
  isHideDisabled: boolean;
  isDeleteDisabled: boolean;
};

function DeleteTextBlockDialog({
  open,
  onOpenChange,
  onCancel,
  onOnlyHide,
  onPermanentDelete,
  isHideDisabled,
  isDeleteDisabled,
}: DeleteTextBlockDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("Textblock löschen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Achtung: Dieser Eintrag wird permanent gelöscht und kann nicht wiederhergestellt werden.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm">
          <div className="mb-2 font-medium">
            {t("Möchten Sie den Textblock wirklich löschen?")}
          </div>
          <div className="text-muted-foreground">
            {t(
              "Wenn Sie den Textblock nur aus der Anzeige entfernen möchten, wählen Sie stattdessen die Option 'Nur ausblenden'.",
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t("Abbrechen")}
          </Button>
          <Button
            variant="secondary"
            onClick={onOnlyHide}
            disabled={isHideDisabled}
          >
            {t("Nur ausblenden")}
          </Button>
          <Button
            variant="destructive"
            onClick={onPermanentDelete}
            disabled={isDeleteDisabled}
          >
            {t("Permanent löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TipTapHtmlEditor({
  value,
  onChange,
  disabled,
  editable = true,
  showToolbar = editable,
}: {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  editable?: boolean;
  showToolbar?: boolean;
}) {
  const isEditable = editable && !disabled;
  const targetValue = value?.trim() ? value : "<p></p>";

  const editor = useEditor(
    {
      content: value,
      editable: isEditable,
      onUpdate: ({ editor: instance }) => {
        if (isEditable && onChange) {
          onChange(editorGenerateHTML(instance));
        }
      },
    },
    [isEditable],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    const current = editorGenerateHTML(editor).trim() || "<p></p>";
    if (current === targetValue) {
      return;
    }
    if (isEditable && editor.isFocused) {
      return;
    }
    editor.commands.setContent(targetValue, { emitUpdate: false });
  }, [editor, isEditable, targetValue]);

  if (!editor) {
    return null;
  }

  return (
    <Editor editor={editor}>
      {showToolbar ? (
        <EditorToolbar>
          <EditorBoldItalicUnderlineGroup />
          <EditorToolbarSeparator />
          <EditorBulletList />
          <EditorOrderedList />
        </EditorToolbar>
      ) : null}
      <EditorContent />
    </Editor>
  );
}
